import moment, { Moment } from 'moment';
import { transformInfoBank } from '../../containers/NegotiationDebts/transformInfoBank';
import { useBrandContext } from '../../dictionaries/context/BankContext';
import { NegotiationExtraInfo, NegotiationOption, NegotiationOptionAlternative, Normative026, OutDebt } from '../../generated/graphql';
import { addBusinessDays } from '../../utils/addBusinessDays';
import { ceilToNext } from '../../utils/ceilToNext';
import { getCommissionMultiplier } from '../../utils/getCommissionMultiplier';
import { getDebtTypeLabel } from '../../utils/getDebtTypeLabel';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { getNegotiationOptionLabel } from '../../utils/getNegotiationOptionLabel';
import thousandSeparator from '../../utils/thousandSeparator';
import { useGetProductNumberDef } from '../../utils/useGetProductNumberDef';
import { useGetMaxPaymentDate } from './useGetMaxPaymentDate';

export interface UseNegotiationStateAlertContextProps {
  outDebt: OutDebt;
  option: NegotiationOption;
  alternative: NegotiationOptionAlternative;
  extraInfo?: NegotiationExtraInfo;
  commissionText?: string;
  commissionPercentBulletText?: string;
  weightedRateText?: string;
}

export const useNegotiationStateAlertContext = (props: UseNegotiationStateAlertContextProps) => {
  const {
    outDebt,
    alternative,
    option,
    extraInfo,
    commissionText = '',
    commissionPercentBulletText = '',
    weightedRateText = '',
  } = props;
  const productNumberDef = useGetProductNumberDef(outDebt);
  const [ brand = '' ] = useBrandContext();

  const {
    isRefinance, isTotalPayment, isRappiPay, isRappiPrestamo, isBbva,
    isPichincha, isRestructure, isBogota, isJcap,
  } = getNegBooleans(outDebt, option, alternative);

  const formatDate = (inputDate?: Date | string | Moment, format = 'LL') => {
    const date = moment(inputDate);
    return date.isValid() ? date.format(format) : '';
  };

  const { bank, type } = transformInfoBank(outDebt);

  let negType = `${option.type}`;

  let businessDaysAdded = 5;
  if (isRappiPay || isRappiPrestamo) businessDaysAdded = 8;
  if (isBbva && isTotalPayment) businessDaysAdded = 36;
  if (
    isBbva && isRefinance && outDebt.extra?.normative026 === Normative026.Restructuring
  ) negType = Normative026.Restructuring;
  if (
    isBogota && isRefinance
  ) negType = 'Novación';
  if (isPichincha && isTotalPayment) businessDaysAdded = 10;
  if (isBogota && isTotalPayment) businessDaysAdded = 10;
  if (isJcap) businessDaysAdded = 8;

  const commissionValue = ((isRappiPay || isRappiPrestamo) && isRestructure)
    ? getCommissionMultiplier(outDebt) * (alternative?.value || 0)
    : (outDebt.extra?.commissionValue || 0) / (alternative?.term || 1);

  const alternativeCommissionPercent = alternative.commissionPercent || 0;

  const commissionPercent = (alternativeCommissionPercent * 100).toFixed(2);

  const rateRaw = (option || outDebt.negotiation?.negotiationOption)?.rate || 0;
  const rate = (rateRaw * 100).toFixed(2);

  const balanceBehind = ceilToNext((outDebt.balanceBehind || outDebt.minPayment || 0)
    * (1 + alternativeCommissionPercent));

  const negPaymentDate = useGetMaxPaymentDate({ option, outDebt, extraInfo });

  return {
    bank,
    brand,
    negType,
    negTypeLabel: getNegotiationOptionLabel(
      option.type,
      outDebt.bank,
      outDebt.extra?.normative026 as Normative026,
      alternative,
      outDebt.brand,
    ),
    debtType: type,
    prePayment: thousandSeparator(alternative.prePayment) || '',
    monthlyPayment: thousandSeparator(alternative.value),
    minPayment: thousandSeparator(outDebt?.minPayment),
    balanceBehind,
    newPaymentDay: extraInfo?.newPaymentDay || moment(option.settings?.maxPaymentDate).date() || '',
    paymentDay: formatDate(outDebt.paymentDate, 'D'),
    productType: getDebtTypeLabel(type),
    negPaymentDate,
    paymentDate: formatDate(outDebt.paymentDate),
    productNumber: productNumberDef,
    childrenDebts: outDebt.extra?.children?.map((ch) => (ch.productNumber)).join(', ') || '',
    createdAt: formatDate(outDebt.negotiation?.createdAt),
    certificationDate: formatDate(addBusinessDays(
      moment(outDebt.negotiation?.updatedAt), businessDaysAdded,
    )),
    paymentValue: thousandSeparator(alternative?.value || 0),
    balance: thousandSeparator(outDebt.balance),
    commissionValue: thousandSeparator(commissionValue),
    commissionPercent: `${commissionPercent}%`,
    rate: `${rate}%`,
    commissionText,
    commissionPercentBulletText,
    weightedRateText,
    issuingEntity: outDebt.extra?.issuingEntity || '',
    otherChargesNonDue: thousandSeparator(outDebt?.extra?.otherChargesNonDue || 0),
  };
};
